<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import { useAccount } from 'dashboard/composables/useAccount';
import { ATTRIBUTE_TYPES } from 'dashboard/components-next/ConversationWorkflow/constants';
import ConversationApi from 'dashboard/api/inbox/conversation';
import MessageApi from 'dashboard/api/inbox/message';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import ConversationWhatsappRescheduleModal from './ConversationWhatsappRescheduleModal.vue';
import ConversationTransferAgentModal from './ConversationTransferAgentModal.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();
const { currentAccount } = useAccount();
const getInbox = useMapGetter('inboxes/getInbox');

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const resolveAttributesModalRef = ref(null);
const rescheduleModalRef = ref(null);
const transferModalRef = ref(null);
const DROPDOWN_SYNC_EVENT = 'chatmigo:conversation-actions-dropdown:open';
const DROPDOWN_SOURCE = 'resolve';

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(DROPDOWN_SYNC_EVENT, {
        detail: { source: DROPDOWN_SOURCE },
      })
    );
  }
  toggleDropdown(true);
};

const handleExternalDropdownOpen = event => {
  if (event?.detail?.source !== DROPDOWN_SOURCE) {
    closeDropdown();
  }
};

const currentChat = computed(() => getters.getSelectedChat.value);
const currentInbox = computed(() => getInbox.value(currentChat.value?.inbox_id));
const isWhatsAppConversation = computed(
  () => currentInbox.value?.channel_type === 'Channel::Whatsapp'
);
const currentInboxId = computed(() => currentChat.value?.inbox_id || 0);
const currentUserId = computed(() => Number(getters.getCurrentUser.value?.id) || 0);
const currentAssigneeId = computed(
  () => Number(currentChat.value?.meta?.assignee?.id) || 0
);
const isAssignedToCurrentUser = computed(
  () => currentAssigneeId.value !== 0 && currentAssigneeId.value === currentUserId.value
);
const canResolveConversation = computed(() => {
  return !isOpen.value || isAssignedToCurrentUser.value;
});

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const resolveClosingFields = computed(() => {
  const settings = currentAccount.value?.settings || {};
  const closingCategories = settings.closing_categories || [];
  const closingTypes = settings.closing_types || [];
  const fields = [];

  if (closingCategories.length) {
    fields.push({
      value: 'closing_category',
      label: t('CONVERSATION_WORKFLOW.CLOSING_FIELDS.CATEGORIES.FIELD_LABEL'),
      type: ATTRIBUTE_TYPES.LIST,
      attributeValues: closingCategories,
    });
  }

  if (closingTypes.length) {
    fields.push({
      value: 'closing_type',
      label: t('CONVERSATION_WORKFLOW.CLOSING_FIELDS.TYPES.FIELD_LABEL'),
      type: ATTRIBUTE_TYPES.LIST,
      attributeValues: closingTypes,
    });
  }

  return fields;
});

const getMissingClosingFields = (conversationCustomAttributes = {}) =>
  resolveClosingFields.value.filter(field => {
    const value = conversationCustomAttributes[field.value];
    return value == null || String(value).trim() === '';
  });

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const openRescheduleModal = () => {
  closeDropdown();
  rescheduleModalRef.value?.open();
};

const openTransferModal = () => {
  closeDropdown();
  transferModalRef.value?.open();
};

const toggleStatus = async (
  status,
  snoozedUntil,
  customAttributes = null,
  scheduledTemplate = null
) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  if (scheduledTemplate) {
    payload.scheduledTemplate = scheduledTemplate;
  }

  try {
    await store.dispatch('toggleStatus', payload);
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  } catch (error) {
    const errorMessage = error?.response?.data?.error;
    useAlert(errorMessage || t('CONVERSATION.CHANGE_STATUS_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      context.scheduledTemplate
        ? wootConstants.STATUS_TYPE.SNOOZED
        : wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes,
      context.scheduledTemplate
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  if (!canResolveConversation.value) {
    useAlert(t('CONVERSATION.MUST_ASSIGN_TO_RESOLVE'));
    return;
  }

  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );
  const missingClosingFields = getMissingClosingFields(currentCustomAttributes);
  const fieldsToResolve = Array.from(
    new Map(
      [...missing, ...missingClosingFields].map(field => [field.value, field])
    ).values()
  );

  if (hasMissing || fieldsToResolve.length > 0) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
      scheduledTemplate: null,
    };
    resolveAttributesModalRef.value?.open(
      fieldsToResolve,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  }
};

const onCmdRescheduleConversation = ({ snoozedUntil, scheduledTemplate }) => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );
  const missingClosingFields = getMissingClosingFields(currentCustomAttributes);
  const fieldsToResolve = Array.from(
    new Map(
      [...missing, ...missingClosingFields].map(field => [field.value, field])
    ).values()
  );

  if (hasMissing || fieldsToResolve.length > 0) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil,
      scheduledTemplate,
    };
    resolveAttributesModalRef.value?.open(
      fieldsToResolve,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(
      wootConstants.STATUS_TYPE.SNOOZED,
      snoozedUntil,
      null,
      scheduledTemplate
    );
  }
};

const createTransferPrivateNote = async note => {
  if (!note || !currentChat.value?.id) return;

  const response = await MessageApi.create({
    conversationId: currentChat.value.id,
    message: note,
    private: true,
  });

  store.dispatch('addMessage', response.data);
};

const onCmdTransferConversation = async ({ agentId, note }) => {
  if (!currentChat.value?.id || !agentId) return;

  closeDropdown();
  isLoading.value = true;

  try {
    const response = await ConversationApi.assignAgent({
      conversationId: currentChat.value.id,
      agentId,
    });
    store.dispatch('setCurrentChatAssignee', {
      conversationId: currentChat.value.id,
      assignee: response.data,
    });
    await createTransferPrivateNote(note);
    useAlert(t('CONVERSATION.CHANGE_AGENT'));
  } catch (error) {
    useAlert(t('CONVERSATION.CHANGE_AGENT_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async event => {
      // Chrome on Windows treats Alt+E as a legacy shortcut for its own
      // menu (Alt+E / Alt+F both open "Customize and control Google
      // Chrome"). Without preventDefault, our resolve action still runs,
      // but Chrome's menu also opens on top of it.
      event.preventDefault();
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener(DROPDOWN_SYNC_EVENT, handleExternalDropdownOpen);
  }
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener(DROPDOWN_SYNC_EVENT, handleExternalDropdownOpen);
  }
});

defineExpose({
  openRescheduleModal,
  openTransferModal,
});
</script>

<template>
  <div class="flex relative z-40 justify-end items-center resolve-actions">
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow outline-1 outline"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
    >
      <Button
        v-if="isOpen"
        :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        :disabled="!canResolveConversation"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="slate"
        no-animation
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        color="slate"
        trailing-icon
        @click.stop="openDropdown"
      />
    </ButtonGroup>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="chatmigo-conversation-actions-menu border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-[90] bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
            ghost
            slate
            sm
            start
            icon="i-lucide-alarm-clock-minus"
            class="w-full"
            @click="() => openSnoozeModal()"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-dot-dashed"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="isWhatsAppConversation && !isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.RESCHEDULE')"
            ghost
            slate
            sm
            start
            icon="i-lucide-calendar-clock"
            class="w-full"
            @click="openRescheduleModal"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.TRANSFER')"
            ghost
            slate
            sm
            start
            icon="i-lucide-arrow-right-left"
            class="w-full"
            @click="openTransferModal"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
    <ConversationWhatsappRescheduleModal
      ref="rescheduleModalRef"
      :inbox-id="currentInboxId"
      @submit="onCmdRescheduleConversation"
    />
    <ConversationTransferAgentModal
      ref="transferModalRef"
      :inbox-id="currentInboxId"
      :current-assignee-id="currentAssigneeId"
      @submit="onCmdTransferConversation"
    />
  </div>
</template>
